import React, { useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { designSystem, reactHookForm } from '@yola/ws-ui';
import { i18next } from '@yola/ws-sdk';
import getCustomHtmlCodeEditorAnnotations from 'src/js/modules/common/helpers/get-custom-html-code-editor-annotations';
import ConnectIntegrationDialogScaffolding from './connect-integration-dialog-scaffolding';

const { StepLine, Step, ControlGroup, CodeEditorField } = designSystem;
const { useForm } = reactHookForm;
const FIELD_NAME = 'verificationTag';

const defaultValidate = () => null;

const ConnectGoogleSearchConsoleDialog = ({
  captions,
  onSubmit,
  onCancel,
  validate = defaultValidate,
}) => {
  const editorRef = useRef(null);

  const { control, setError, clearErrors, getValues } = useForm({
    defaultValues: {
      [FIELD_NAME]: '',
    },
  });

  const onCodeEditorError = useCallback(
    (error) => {
      if (error) {
        setError(FIELD_NAME, { type: 'manual', message: error });
      } else {
        clearErrors(FIELD_NAME);
      }
    },
    [setError, clearErrors]
  );

  const onValidate = useCallback(
    ({ value, session }) =>
      getCustomHtmlCodeEditorAnnotations({
        value,
        session,
      }),
    []
  );

  const handleSubmit = useCallback(() => {
    if (!editorRef.current) return;

    const errorAnnotation = editorRef.current
      .getSession()
      .getAnnotations()
      .find((annotation) => annotation.type === 'error');

    if (errorAnnotation) {
      const { text, row, column } = errorAnnotation;
      setError(FIELD_NAME, {
        type: 'manual',
        message: `Line ${row + 1}, Column ${column}: ${text}`,
      });
      return;
    }

    const trimmedValue = getValues(FIELD_NAME)?.trim();
    const validationMessage = validate(trimmedValue);

    if (validationMessage) {
      setError(FIELD_NAME, { type: 'manual', message: validationMessage });
      return;
    }

    onSubmit(trimmedValue);
  }, [getValues, validate, onSubmit, setError]);

  const onAfterEditorConfigured = useCallback((editor) => {
    editorRef.current = editor;
  }, []);

  return (
    <ConnectIntegrationDialogScaffolding
      captions={{
        title: i18next.t('Connect {integrationType}', { integrationType: captions.name }),
        about: captions.about,
        notes: captions.notes,
      }}
      onCancel={onCancel}
      onSubmit={handleSubmit}
    >
      <StepLine>
        <Step marker={1}>
          <ControlGroup title={captions.step1.title} descriptionHTML={captions.step1.description} />
        </Step>
        <Step marker={2}>
          <ControlGroup title={captions.step2.title}>
            <CodeEditorField
              name={FIELD_NAME}
              control={control}
              options={{
                showLineNumbers: true,
                tabSize: 2,
                autoScrollEditorIntoView: true,
              }}
              onEditorError={onCodeEditorError}
              onValidate={onValidate}
              onAfterEditorConfigured={onAfterEditorConfigured}
            />
          </ControlGroup>
        </Step>
      </StepLine>
    </ConnectIntegrationDialogScaffolding>
  );
};

ConnectGoogleSearchConsoleDialog.propTypes = {
  captions: PropTypes.shape({
    name: PropTypes.string.isRequired,
    about: PropTypes.string.isRequired,
    step1: PropTypes.shape().isRequired,
    step2: PropTypes.shape().isRequired,
    control: PropTypes.shape().isRequired,
    notes: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  validate: PropTypes.func,
};

export default ConnectGoogleSearchConsoleDialog;
